/**
 * @fileoverview Registro do service worker da PWA — PRD-022 INC-1.
 *
 * Registra `public/sw.js` apenas quando:
 *  - a flag `FEATURE_FLAGS.pwa.installEnabled` está ligada (kill switch), e
 *  - o browser suporta `serviceWorker`, e
 *  - estamos em contexto seguro (`window.isSecureContext` — HTTPS ou localhost).
 *
 * SW não faz sentido fora de HTTPS; em prod o admin roda sob HTTPS. Em dev, o
 * `serve` em localhost também é considerado contexto seguro pelo browser.
 */
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { logger } from "@/utils/logger";

export function registerServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  // NUNCA em desenvolvimento. O SW é cache-first de estáticos e, como os bundles do
  // dev-server não têm hash no nome (`/js/app.js`), ele serviria o bundle ANTIGO em
  // cache a cada load — exigindo hard-refresh a cada mudança. SW é feature de produção.
  // Além de não registrar, desregistra qualquer SW de sessão anterior (auto-cura: uma
  // recarga e o dev volta a servir sempre fresco, sem hard-refresh).
  if (process.env.NODE_ENV !== "production") {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister()))
      .catch(() => {});
    return;
  }

  if (!FEATURE_FLAGS.pwa?.installEnabled) return;
  if (typeof window === "undefined" || !window.isSecureContext) return;

  window.addEventListener("load", () => {
    const base = process.env.BASE_URL || "/";
    // Versão de build no query → o SW re-instala a cada deploy (novo SHA) e o
    // `activate` purga os caches da versão anterior. Ver public/sw.js.
    const version = process.env.VUE_APP_VERSION || "dev";
    const swUrl = `${base}sw.js?v=${encodeURIComponent(version)}`;

    // NÃO recarrega sozinho no `controllerchange` (BUG-051).
    //
    // Recarregava, e em silêncio. Numa tela operada com atendimento em andamento,
    // um reload no meio de uma ação perde o que a pessoa estava fazendo — e ela
    // não tem como saber por que a página "piscou".
    //
    // E era redundante: o `controllerchange` só dispara depois de um carregamento
    // de página, que é quando a URL do SW muda de SHA; a navegação é network-first,
    // então essa página já subiu na build nova. Recarregava para chegar onde já
    // estava.
    //
    // Quem avisa agora é o AppUpdateBanner (useAppVersion), que descobre a build
    // nova SEM depender de navegação — que é o buraco de verdade — e deixa a hora
    // de recarregar com o operador.

    navigator.serviceWorker.register(swUrl).then(
      (registration) =>
        logger.debug("[PWA] service worker registrado", registration.scope),
      (error) => logger.error("[PWA] falha ao registrar service worker", error)
    );
  });
}
